import React from "react";
import { Link } from "react-router-dom";
import ArticleLayout, { H2, DataTable } from "./ArticleLayout";

export default function ArticleSSClaimingAge() {
  return (
    <ArticleLayout
      meta={{
        slug: "social-security-claiming-age",
        hub: "Social Security & Income",
        time: "7 min read",
        title: "Social Security at 62 vs 67 vs 70: Claiming-Age Comparison | Centennial",
        seoDescription:
          "Claiming Social Security at 62 cuts your benefit ~30%; waiting to 70 grows it ~24% past full retirement age. Side-by-side comparison, break-even ages, and how to model it free.",
        h1: "Social Security at 62 vs 67 vs 70",
        hook: "The claiming decision can swing your lifetime benefits by six figures — and it's permanent. Here's the comparison most people never see laid out.",
        quickAnswer:
          "Claiming at 62 permanently reduces your monthly benefit by about 30% versus full retirement age (67 for anyone born 1960 or later). Waiting past 67 adds 8% per year until 70 — about 24% more. The break-even point between claiming at 62 and 70 is typically around age 80–81: if you expect to live longer (and you're planning to 100), delaying usually wins. Claim early if you need the income, have poor health, or want to preserve investments during a downturn.",
      }}
    >
      <H2>The same benefit, three very different checks</H2>
      <p>
        Suppose your full-retirement-age (67) benefit is $2,000/month. Here's what each claiming
        age actually pays (Social Security Administration reduction and delayed-credit rules):
      </p>
      <DataTable
        head={["Claim at", "Monthly check", "Vs. full benefit", "Annual income"]}
        rows={[
          ["62", "$1,400", "−30%", "$16,800"],
          ["67 (FRA)", "$2,000", "—", "$24,000"],
          ["70", "$2,480", "+24%", "$29,760"],
        ]}
      />
      <p>
        The difference between 62 and 70 is $1,080 per month — every month, for life, inflation
        adjusted.
      </p>

      <H2>Break-even math</H2>
      <p>
        Claiming early means more years of smaller checks; claiming late means fewer years of
        bigger ones. The crossover typically lands around age 80–81. Average life expectancy for
        a 65-year-old American is roughly 84 (men) and 86–87 (women) — and if you're using a
        calculator that plans to age 100, the odds favor delay for the healthy.
      </p>

      <H2>Why delaying is really longevity insurance</H2>
      <p>
        The biggest financial risk in a 100-year plan isn't dying early — it's living long after
        your portfolio is tired. A benefit claimed at 70 is the cheapest inflation-protected
        lifetime annuity you can buy, because you "buy" it by spending a few years of savings.
        For married couples, the higher earner's delayed benefit also becomes the survivor
        benefit — protection that outlives either spouse.
      </p>

      <H2>Good reasons to claim early anyway</H2>
      <p>
        You need the income now. Your health or family history argues against the actuarial
        averages. You'd otherwise sell investments in a deep bear market —{" "}
        <Link className="text-[#2B5339] underline" to="/learn/sequence-risk">sequence risk</Link>{" "}
        can make early claiming the portfolio-saving move. A lower-earning spouse claiming early
        while the higher earner delays is a common hybrid strategy.
      </p>

      <H2>Model both choices in minutes</H2>
      <p>
        The Centennial calculator lets you add Social Security as an income stream with any
        start age — run one plan claiming at 62 and another at 70, then{" "}
        <Link className="text-[#2B5339] underline" to="/compare">compare them side by side</Link>{" "}
        through 5,000 market simulations. Free, no signup. Your official estimate lives at
        ssa.gov/myaccount.
      </p>
    </ArticleLayout>
  );
}
